import {
  getMessageAuthorUserId,
  isInternalNoteType,
  isLogType,
} from "../components/MessageHistory/messageTypeRegistry";
import { getMessageDate, sortMessagesChronologically } from "../components/MessageHistory/timelineUtils";
import { getChannelById, getChannelTypeById } from "./utils";
import { deriveConversationContext } from "./conversationContextUtils";
import { canUseClientChannelForConversation } from "./clientContactUtils";
import { isActiveChannel, isComposerOutboundChannel } from "../constants/channelCatalog";

/**
 * Canal a restaurar ao sair de anotação interna (toggle Responder cliente).
 * Ignora `internal_note` na sessão para não curto-circuitar o fallback da solicitação.
 */
export const resolveClientComposeChannelRestore = ({
  lastClientChannel,
  savedChannel,
  conversationChannelId,
  defaultChannelId,
} = {}) => {
  const candidates = [
    lastClientChannel,
    savedChannel,
    conversationChannelId,
    defaultChannelId,
  ];
  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    if (String(candidate) === "internal_note") continue;
    return candidate;
  }
  return null;
};

/**
 * Canal de envio padrão da solicitação, em ordem: canal da última mensagem do
 * cliente, canal de origem, canal mais recente da thread e, por fim, um canal do
 * mesmo tipo da origem. Só considera tipos com envio real (não DEFAULT/Ads/Vídeo)
 * e canais ativos. Retorna null quando o workspace não tem canal de envio.
 */
export const resolveDefaultReplyChannel = (messagesList, conversation, channels) => {
  const leadChannelId = conversation?.channelId;
  const lastClientChannelId =
    deriveConversationContext(messagesList).lastClientMessage?.messageChannelId;

  const isMessagingChannel = (channelId) => {
    const ch = getChannelById(channels, channelId);
    if (!ch) return false;
    return isComposerOutboundChannel(ch) && isActiveChannel(ch);
  };

  if (
    lastClientChannelId != null &&
    lastClientChannelId !== "" &&
    Number(lastClientChannelId) > 0 &&
    isMessagingChannel(lastClientChannelId)
  ) {
    const lastClientChannel = getChannelById(channels, lastClientChannelId);
    const lastClientChannelType =
      lastClientChannel?.channelType || getChannelTypeById(channels, lastClientChannelId);
    if (
      lastClientChannelType &&
      canUseClientChannelForConversation(
        conversation,
        lastClientChannelId,
        lastClientChannelType,
        lastClientChannel?.tipo,
        messagesList
      )
    ) {
      return lastClientChannelId;
    }
  }

  if (leadChannelId != null && isMessagingChannel(leadChannelId)) {
    const leadCh = getChannelById(channels, leadChannelId);
    const leadType = leadCh?.channelType || getChannelTypeById(channels, leadChannelId);
    if (
      canUseClientChannelForConversation(
        conversation,
        leadChannelId,
        leadType,
        leadCh?.tipo,
        messagesList
      )
    ) {
      return leadChannelId;
    }
  }

  // Sem origem utilizável, o canal mais recente da thread erra menos que um canal
  // qualquer do workspace.
  const list = Array.isArray(messagesList) ? messagesList : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const id = list[i]?.messageChannelId;
    if (id == null || id === "" || Number(id) <= 0 || !isMessagingChannel(id)) continue;
    const ch = getChannelById(channels, id);
    const type = ch?.channelType || getChannelTypeById(channels, id);
    if (canUseClientChannelForConversation(conversation, id, type, ch?.tipo, messagesList)) {
      return id;
    }
  }

  // Último recurso: a ordem da API varia (última alteração do canal), então
  // preferimos o mesmo tipo da origem e, no empate, o menor id.
  const outboundChannels = (channels || [])
    .filter((ch) => isComposerOutboundChannel(ch) && isActiveChannel(ch))
    .filter((ch) =>
      canUseClientChannelForConversation(
        conversation,
        ch.id,
        ch.channelType,
        ch.tipo,
        messagesList
      )
    )
    .sort((a, b) => Number(a.id) - Number(b.id));
  const leadChannel = getChannelById(channels, leadChannelId);
  const sameTypeAsLead =
    leadChannel?.tipo != null
      ? outboundChannels.find((ch) => Number(ch.tipo) === Number(leadChannel.tipo))
      : null;
  return (sameTypeAsLead ?? outboundChannels[0])?.id ?? null;
};

const stripHtml = (html) =>
  typeof html === "string" ? html.replace(/<[^>]*>/g, "").trim() : "";

const normalizePersonName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getConversationAssigneeUserId = (conversation) => {
  const raw =
    conversation?.userId ??
    conversation?.user_id ??
    conversation?.idUsuario ??
    conversation?.id_usuario;
  if (raw == null || raw === "" || Number(raw) === 0) return null;
  return String(raw);
};

const getLogPlainText = (message) =>
  message?.textPlain || stripHtml(message?.text || "");

const isTransferLogMessage = (message) => {
  if (!isLogType(message)) return false;
  if (message?.logMeta?.code === "transfer_user") return true;
  const plain = getLogPlainText(message).toLowerCase();
  return /transferida|atribu[ií]da/.test(plain);
};

const labelMatchesUser = (label, userName, userId) => {
  const normalizedLabel = normalizePersonName(label);
  if (!normalizedLabel) return false;

  const normalizedName = normalizePersonName(userName);
  if (normalizedName) {
    if (normalizedLabel === normalizedName) return true;
    if (normalizedLabel.includes(normalizedName) || normalizedName.includes(normalizedLabel)) {
      return true;
    }
  }

  if (userId != null && /\(id\s+\d+\)/i.test(String(label))) {
    const match = String(label).match(/\(id\s+(\d+)\)/i);
    if (match && String(match[1]) === String(userId)) return true;
  }

  return false;
};

/** Transferência/atribuição recente cujo destino é o usuário logado. */
export const isTransferLogToUser = (message, userId, userName) => {
  if (!isTransferLogMessage(message)) return false;

  const toRef = message?.logMeta?.to;
  if (toRef) {
    if (toRef.type === "user" && toRef.id != null && String(toRef.id) === String(userId)) {
      return true;
    }
    if (labelMatchesUser(toRef.label, userName, userId)) return true;
  }

  const plain = getLogPlainText(message);
  const normalizedPlain = plain.toLowerCase();
  const normalizedName = normalizePersonName(userName);
  if (!normalizedName) return false;

  const toPatterns = [
    /atribu[ií]da\s+para(?:\s+o|\s+a)?\s+atendente\s+(.+?)(?:\.|$)/i,
    /transferida\s+do\s+.+?\s+para\s+o\s+atendente\s+(.+?)(?:\.|$)/i,
    /transferida\s+do\s+chatbot\s+.+?\s+para\s+o\s+atendente\s+(.+?)(?:\.|$)/i,
    /transferida\s+do\s+agente\s+de\s+ia\s+.+?\s+para\s+o\s+atendente\s+(.+?)(?:\.|$)/i,
  ];

  return toPatterns.some((pattern) => {
    const match = normalizedPlain.match(pattern);
    if (!match?.[1]) return false;
    return labelMatchesUser(match[1], userName, userId);
  });
};

const findLatestTransferToUser = (messages, userId, userName) => {
  const sorted = sortMessagesChronologically(messages);
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const msg = sorted[i];
    if (isTransferLogToUser(msg, userId, userName)) return msg;
  }
  return null;
};

const isHandoffNoteFromPreviousUser = (note, transferLog, myUserId, transferDate) => {
  const authorId = getMessageAuthorUserId(note);
  if (!authorId || authorId === myUserId) return false;

  const noteDate = getMessageDate(note);
  const actorId =
    transferLog?.logMeta?.actorUserId != null && Number(transferLog.logMeta.actorUserId) > 0
      ? String(transferLog.logMeta.actorUserId)
      : null;

  if (actorId && authorId === actorId) return true;

  if (transferDate && noteDate) {
    if (noteDate >= transferDate) return true;
    if (actorId && authorId === actorId) {
      const deltaMs = transferDate.getTime() - noteDate.getTime();
      if (deltaMs >= 0 && deltaMs <= 30 * 60 * 1000) return true;
    }
  }

  return false;
};

/**
 * Solicitação transferida/atribuída ao usuário logado com anotação interna
 * deixada pelo atendente anterior (handoff).
 */
export const shouldDefaultToInternalNoteForTransferHandoff = (
  messages = [],
  conversation,
  currentUser
) => {
  const myUserId =
    currentUser?.id != null && Number(currentUser.id) > 0 ? String(currentUser.id) : null;
  if (!myUserId) return false;

  const assigneeId = getConversationAssigneeUserId(conversation);
  if (!assigneeId || assigneeId !== myUserId) return false;

  const userName = currentUser?.nome || currentUser?.name || "";
  const transferLog = findLatestTransferToUser(messages, myUserId, userName);
  if (!transferLog) return false;

  const transferDate = getMessageDate(transferLog);
  const sorted = sortMessagesChronologically(messages);

  return sorted.some(
    (msg) =>
      isInternalNoteType(msg) &&
      isHandoffNoteFromPreviousUser(msg, transferLog, myUserId, transferDate)
  );
};
